import type { Card, Envelope, ShEvent, ShTableState, Suit } from "../gen/bazivan/v1/table_pb";
import { client } from "../net/client";
import { signal } from "../net/signal";
import { showCarpet } from "../game/carpet";
import { Sequencer } from "../scene/queue";
import { introGate } from "../game/stake";
import { applyShelem, canPlay, emptyShelem, sameCard, ShPhase, toLocal, type ShelemLocal } from "./state";
import { asHokmView } from "./view";
import type { HokmScene } from "../hokm/scene";

/** پایانِ دست یا مسابقه — پنجره‌ی نتایج را ری‌اکت می‌کشد. */
export type ShOverlay =
  | { kind: "hand"; winnerUnit: number; bid: number; roundScore: number[]; shelem: boolean; kot: boolean; yasa: boolean }
  | { kind: "match"; winnerUnit: number };

/**
 * کنترلرِ میزِ آنلاینِ شلم.
 *
 * هیچ قاعده‌ای این‌جا اجرا نمی‌شود؛ حقیقت همیشه ShTableStateِ سرور است. سه کار:
 * ترجمه‌ی وضعیت، انیمیتِ رویدادها به ترتیب، و تبدیلِ لمسِ بازیکن به فرمان.
 *
 * فرقش با حکم و چهاربرگ: بازیکن چهار جور ورودی دارد و نه یکی — خواندن، پاس،
 * واگذاریِ چهار برگ، اعلامِ حکم، و انداختنِ کارت. کدامشان باز باشد را **سرور**
 * می‌گوید (awaiting*FromMe از روی phase)، نه حدسِ کلاینت؛ وگرنه صفحه چیزی را
 * می‌پرسید که میز نمی‌پذیرفت.
 */
export class ShelemOnline {
  private seq = new Sequencer();
  private scene: HokmScene;
  private off: (() => void) | null = null;
  private gotState = false;
  private seatsLaid = false;
  private quitting = false;
  private carpetApplied = false;
  private ceremonyDone = false;
  private startShown = false;
  private st: ShelemLocal = emptyShelem();
  private turnEndsAt = 0;
  private turnTotal = 0;
  private tick: ReturnType<typeof setInterval> | null = null;

  readonly overlay = signal<ShOverlay | null>(null);
  readonly raw = signal<ShTableState | null>(null);
  /** چهار برگِ زمین که همین حالا به دستِ حاکم آمد — فقط برای نشان‌دادن. */
  readonly widow = signal<Card[] | null>(null);
  /** برگ‌هایی که حاکم برای واگذاری انتخاب کرده؛ ری‌اکت می‌کشدشان. */
  readonly picked = signal<Card[]>([]);

  constructor(scene: HokmScene) { this.scene = scene; }
  get state(): ShelemLocal { return this.st; }

  start(): void {
    this.off = client.on((env) => this.onMessage(env));
    // پرده‌ی شرط بالاست؟ صحنه تا بالا رفتنش چیزی نمی‌کشد — [[CbOnline.start]].
    this.seq.hold(introGate());
    const ace = client.shAce.value;
    if (ace) this.seq.enqueue(() => this.playEvent(ace));
    const deal = client.shDeal.value;
    if (deal) this.seq.enqueue(() => this.playEvent(deal));
    const w = client.shWidow.value;
    if (w) this.seq.enqueue(() => this.playEvent(w));
    const last = client.shTable.value;
    if (last) this.seq.enqueueSnapshot(() => this.applyState(last));
    this.tick = setInterval(() => this.updateTimer(), 200);
    (window as unknown as { __shelem?: ShelemOnline }).__shelem = this;
  }

  dispose(): void {
    this.quitting = true;
    this.off?.(); this.off = null;
    this.seq.dispose();
    if (this.tick) clearInterval(this.tick);
  }

  // ---------------------------------------------------------------- ورودیِ بازیکن

  /** خواندن. سرور دوباره می‌سنجدش؛ این‌جا فقط جلوی دکمه‌ی بی‌معنا گرفته می‌شود. */
  bid(amount: number): void {
    if (this.quitting || !this.st.awaitingBidFromMe) return;
    if (amount < this.st.bidFloor || amount > 165 || amount % 5 !== 0) return;
    client.shBid(amount);
  }

  pass(): void {
    if (this.quitting || !this.st.awaitingBidFromMe) return;
    if (this.st.mustBid) { this.scene.toast("شروع‌کننده باید بخوانَد"); return; }
    client.shPass();
  }

  /** لمسِ کارت: در واگذاری انتخاب/لغوِ انتخاب، در بازی انداختن. */
  clickCard(i: number): void {
    if (this.quitting) return;
    const card = this.st.myHand[i];
    if (!card) return;

    if (this.st.awaitingDiscardFromMe) {
      const cur = this.picked.value;
      const at = cur.findIndex((c) => sameCard(c, card));
      if (at >= 0) { this.picked.value = cur.filter((_, k) => k !== at); return; }
      if (cur.length >= 4) { this.scene.toast("چهار برگ بس است"); return; }
      this.picked.value = [...cur, card];
      return;
    }

    if (!this.st.myTurn) return;
    if (!canPlay(this.st, card)) { this.scene.toast("باید از خالِ زمینه بیایی"); return; }
    this.turnEndsAt = 0;
    client.shPlay(card);
  }

  /** واگذاریِ چهار برگِ انتخاب‌شده. */
  discard(): void {
    if (this.quitting || !this.st.awaitingDiscardFromMe) return;
    const p = this.picked.value;
    if (p.length !== 4) { this.scene.toast("چهار برگ انتخاب کن"); return; }
    this.picked.value = [];
    this.widow.value = null;
    client.shDiscard(p);
  }

  callTrump(suit: Suit): void {
    if (this.quitting || !this.st.awaitingTrumpFromMe) return;
    client.shTrump(suit);
  }

  nextHand(): void { this.overlay.value = null; }
  seatName(l: number): string { return l === 0 ? "تو" : (this.st.names[l] || "حریف"); }

  // ---------------------------------------------------------------- پیام‌های سرور

  private onMessage(env: Envelope): void {
    if (this.quitting) return;
    switch (env.msg.case) {
      case "shState": { const ts = env.msg.value; this.seq.enqueueSnapshot(() => this.applyState(ts)); break; }
      case "shEvent": { const ev = env.msg.value; this.seq.enqueue(() => this.playEvent(ev)); break; }
      case "error": this.showError(env.msg.value.code); break;
    }
  }

  private showError(code: string): void {
    const msg: Record<string, string> = {
      not_your_turn: "نوبتِ تو نیست",
      not_in_hand: "این کارت در دستت نیست",
      follow_suit: "باید از خالِ زمینه بیایی",
      low_bid: "باید بالاتر از خواندنِ فعلی بخوانی",
      bid_step: "خواندن پله‌ی پنج‌تایی دارد",
      bid_range: "خواندن بینِ ۱۰۰ و ۱۶۵ است",
      must_bid: "شروع‌کننده باید بخوانَد",
      already_passed: "پاس داده‌ای",
      not_declarer: "این کار مالِ حاکم است",
      discard_count: "چهار برگ باید بگذاری",
      discard_dup: "یک کارت را دو بار نمی‌شود گذاشت",
      wrong_phase: "الان وقتش نیست",
    };
    if (msg[code]) this.scene.toast(msg[code]!);
  }

  private async applyState(ts: ShTableState): Promise<void> {
    this.st = applyShelem(ts);
    this.raw.value = ts;
    this.gotState = true;

    if (!this.carpetApplied && this.st.carpet) { showCarpet(this.st.carpet); this.carpetApplied = true; }
    const v = asHokmView(this.st);
    if (!this.seatsLaid) { this.scene.layoutSeats(v); this.seatsLaid = true; }
    this.scene.render(v);
    this.scene.renderTrick(v);
    this.scene.updateScores(v);
    this.scene.setTurn(v);
    this.refreshPlayable();
    // مدالِ حکم از هر وضعیت از نو ساخته می‌شود — [[HokmScene.syncTrump]].
    this.scene.syncTrump(this.st.trumpChosen, this.st.trump);

    // انتخابِ واگذاری فقط تا وقتی که سرور هنوز می‌پرسد معنا دارد.
    if (!this.st.awaitingDiscardFromMe && this.picked.value.length > 0) this.picked.value = [];
    if (this.st.phase !== ShPhase.DISCARD && this.widow.value) this.widow.value = null;

    if (this.overlay.value && !this.st.handOver && !this.st.matchOver) this.overlay.value = null;

    const asking = this.st.myTurn || this.st.awaitingBidFromMe ||
      this.st.awaitingDiscardFromMe || this.st.awaitingTrumpFromMe;
    if (asking && this.st.turnRemaining > 0.1) {
      this.turnTotal = Math.max(this.turnTotal, this.st.turnRemaining);
      this.turnEndsAt = performance.now() + this.st.turnRemaining * 1000;
    } else { this.turnEndsAt = 0; this.turnTotal = 0; this.scene.setTimer(0, 0); }
  }

  private refreshPlayable(): void {
    if (this.st.awaitingDiscardFromMe) {
      // در واگذاری هر برگی برداشتنی است.
      this.scene.markPlayable(this.st.myHand.map(() => true));
      return;
    }
    if (!this.st.myTurn) { this.scene.markPlayable(this.st.myHand.map(() => false)); return; }
    this.scene.markPlayable(this.st.myHand.map((c) => canPlay(this.st, c)));
  }

  private updateTimer(): void {
    if (this.turnEndsAt <= 0) return;
    const left = (this.turnEndsAt - performance.now()) / 1000;
    if (left <= 0) { this.turnEndsAt = 0; this.scene.setTimer(0, 0); return; }
    this.scene.setTimer(left, Math.max(left, this.turnTotal));
  }

  private async playEvent(ev: ShEvent): Promise<void> {
    const n = this.st.n;
    const local = toLocal(ev.seat, this.st.me, n);
    switch (ev.kind) {
      case "ace_draw": {
        if (this.ceremonyDone || ev.draw.length === 0) return;
        this.ceremonyDone = true;
        const fresh = client.shTable.value;
        if (fresh) { this.st = applyShelem(fresh); this.raw.value = fresh; this.gotState = true; }
        const v = asHokmView(this.st);
        if (!this.seatsLaid) { this.scene.layoutSeats(v); this.seatsLaid = true; }
        this.scene.hideHand(true);
        this.scene.render(v);
        const from = toLocal(ev.fromSeat, this.st.me, n);
        await this.scene.animStarterDraw(ev.draw, from, local, this.seatName(local));
        await this.scene.animShuffle();
        this.scene.hideHand(false);
        this.scene.render(asHokmView(this.st));
        client.shAce.value = null;
        break;
      }

      case "deal": {
        const fresh = client.shTable.value;
        if (fresh) { this.st = applyShelem(fresh); this.raw.value = fresh; this.gotState = true; }
        if (!this.gotState) break;
        const v = asHokmView(this.st);
        if (!this.carpetApplied && this.st.carpet) { showCarpet(this.st.carpet); this.carpetApplied = true; }
        if (!this.seatsLaid) { this.scene.layoutSeats(v); this.seatsLaid = true; }
        await this.scene.animDealBatch(v, local, Math.min(12, Math.max(1, this.st.myHand.length || 12)));
        client.shDeal.value = null;
        break;
      }

      case "bid":
        this.scene.toast(`${this.seatName(local)}: ${fa(ev.amount)}`);
        break;
      case "pass":
        this.scene.toast(`${this.seatName(local)}: پاس`);
        break;
      case "declarer":
        this.scene.toast(`حاکم ${this.seatName(local)} با ${fa(ev.amount)}`);
        break;

      case "widow":
        // فقط به خودِ حاکم می‌رسد؛ ری‌اکت نشانش می‌دهد تا بداند چه برداشته.
        this.widow.value = [...ev.cards];
        client.shWidow.value = null;
        break;

      case "discard":
        if (local === 0) this.widow.value = null;
        break;

      case "trump_called": {
        await this.scene.animTrumpAnnounce(ev.suit);
        if (!this.startShown) { this.startShown = true; await this.scene.animStartBanner("شروع!"); }
        break;
      }

      case "played": {
        if (!ev.card) return;
        const card = ev.card;
        if (local === 0 && !this.st.watching) {
          const at = this.st.myHand.findIndex((c) => sameCard(c, card));
          if (at >= 0) this.st.myHand.splice(at, 1);
          this.st.counts[0] = this.st.myHand.length;
        } else if (local >= 0 && local < n && (this.st.counts[local] ?? 0) > 0) this.st.counts[local]!--;
        this.scene.render(asHokmView(this.st));
        await this.scene.animPlayToTrick(local, card);
        if (this.st.trickLeader < 0) { this.st.trickLeader = local; this.st.ledSuit = card.suit; this.st.trickHasLead = true; }
        this.st.trick[local] = card;
        this.scene.renderTrick(asHokmView(this.st));
        break;
      }

      case "trick_won": {
        if (local < 0 || local >= n) return;
        await this.scene.animCollectTrick(asHokmView(this.st), local);
        this.st.trick.fill(null);
        this.st.trickLeader = -1;
        this.st.trickHasLead = false;
        this.scene.renderTrick(asHokmView(this.st));
        break;
      }

      case "hand_over":
        this.overlay.value = {
          kind: "hand", winnerUnit: localUnit(ev.winnerUnit, this.st.me),
          bid: ev.amount, roundScore: rotate(ev.roundScore, this.st.me),
          shelem: ev.shelem, kot: ev.kot, yasa: ev.yasa,
        };
        break;

      case "match_over":
        this.overlay.value = { kind: "match", winnerUnit: localUnit(ev.winnerUnit, this.st.me) };
        break;

      case "seat_bot":
        this.scene.toast(`${this.seatName(local)} رفت — ربات جایش نشست`);
        break;
      case "seat_reconnect":
        this.scene.toast(`${this.seatName(local)} برگشت`);
        break;
    }
  }
}

const UNITS = 2;
const localUnit = (serverUnit: number, me: number) => ((serverUnit - (me % UNITS)) % UNITS + UNITS) % UNITS;

/** امتیازِ دور را به ترتیبِ واحدِ محلی می‌چیند. */
function rotate(score: number[], me: number): number[] {
  const out = new Array<number>(UNITS).fill(0);
  for (let u = 0; u < UNITS; u++) out[localUnit(u, me)] = score[u] ?? 0;
  return out;
}

const faDigits = "۰۱۲۳۴۵۶۷۸۹";
const fa = (n: number) => String(n).replace(/\d/g, (d) => faDigits[+d]!);
